import { useEffect, useRef, useState } from 'react';

const CONTACT_INFO = [
  { icon: 'ri-phone-line', label: '전화 문의', value: '010-5691-7704', sub: '평일 09:00 - 18:00' },
  { icon: 'ri-mail-line', label: '이메일 문의', value: 'djrwp93@naver.com', sub: '현장 출장 상담 가능' },
];

const SERVICE_TYPES = ['페인트 시공', '도장 서비스', '방수 공사', '특수 도장', '기타 문의'];

export default function ContactSection() {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    serviceType: '',
    address: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.serviceType) {
      alert('이름, 연락처, 시공 유형은 필수 입력항목입니다.');
      return;
    }
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d72a4l1sakbatd8b3g40', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      alert('제출 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={ref} className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Info */}
          <div
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
            }`}
          >
            <span className="text-blue-600 text-sm font-bold tracking-widest uppercase">Contact</span>
            <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mt-2 mb-4">
              무료 견적 신청
            </h2>
            <p className="text-stone-500 text-lg leading-relaxed mb-8">
              라인 인테리어에 문의해 주시면<br />
              24시간 이내로 전문 상담사가 직접 연락드립니다.
            </p>

            <div className="flex flex-col gap-5 mb-8">
              {CONTACT_INFO.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-full bg-blue-50 flex-shrink-0">
                    <i className={`${item.icon} text-blue-600 text-lg`} />
                  </div>
                  <div>
                    <p className="text-sm text-stone-400 font-medium mb-0.5">{item.label}</p>
                    <p className="text-stone-900 font-semibold text-base">{item.value}</p>
                    <p className="text-sm text-stone-400 mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              {['무료 방문 견적', 'A/S 평생 보증', '친환경 도료 사용', '책임 시공'].map((b) => (
                <span
                  key={b}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-stone-100 text-stone-700 text-sm font-medium rounded-full"
                >
                  <i className="ri-check-line text-blue-600" />
                  {b}
                </span>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div
            className={`transition-all duration-700 delay-150 ${
              visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <div className="border border-stone-200 rounded-xl p-6 md:p-8">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-blue-100 mx-auto mb-4">
                    <i className="ri-check-double-line text-blue-600 text-3xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-900 mb-2">견적 신청 완료!</h3>
                  <p className="text-stone-500 text-base leading-relaxed">
                    감사합니다. 24시간 이내로<br />전문 상담사가 직접 연락드리겠습니다.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', phone: '', email: '', serviceType: '', address: '', message: '' }); }}
                    className="mt-6 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    새 견적 신청하기
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="quote-form"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  <h3 className="text-xl font-bold text-stone-900 mb-1">무료 견적 신청서</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">이름 <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="홍길동"
                        required
                        className="w-full h-11 px-4 bg-stone-50 rounded-lg text-sm text-stone-800 border border-stone-200 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-stone-700 mb-1.5">연락처 <span className="text-red-500">*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="010-0000-0000"
                        required
                        className="w-full h-11 px-4 bg-stone-50 rounded-lg text-sm text-stone-800 border border-stone-200 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">이메일</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="example@email.com"
                      className="w-full h-11 px-4 bg-stone-50 rounded-lg text-sm text-stone-800 border border-stone-200 focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">시공 유형 <span className="text-red-500">*</span></label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      required
                      className="w-full h-11 px-4 bg-stone-50 rounded-lg text-sm text-stone-800 border border-stone-200 focus:outline-none focus:border-blue-400 cursor-pointer"
                    >
                      <option value="">시공 유형 선택</option>
                      {SERVICE_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">시공 주소</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="서울시 마포구 ..."
                      className="w-full h-11 px-4 bg-stone-50 rounded-lg text-sm text-stone-800 border border-stone-200 focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-stone-700 mb-1.5">상세 내용</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="시공 관련 상세 내용을 입력해주세요 (면적, 현황 등)"
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-stone-50 rounded-lg text-sm text-stone-800 border border-stone-200 focus:outline-none focus:border-blue-400 resize-none"
                    />
                    <p className="text-xs text-stone-400 text-right mt-1">{formData.message.length}/500</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold rounded-lg text-base transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" /> 제출 중...
                      </>
                    ) : (
                      <>
                        무료 견적 신청하기 <i className="ri-arrow-right-line" />
                      </>
                    )}
                  </button>
                  <p className="text-sm text-stone-400 text-center">
                    * 개인정보는 견적 상담 목적으로만 사용됩니다
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
